import {
    IAggregatedQuestion,
    ILevelMap,
    ILevelMapAggregatedTile, ILevelMapProgress,
    ILevelMapTileProgress,
    TileType
} from "../../types";
import {call, fork, put, select} from "redux-saga/effects";
import {IApplicationState} from "../../../index";
import {setLevelProgress} from "../../actions";
import makeMap from "../../../../shared/utils/makeMap";
import {preloadTileImages} from "./preloadTileImages";

function* revealTileIfNeeded(tile: ILevelMapAggregatedTile, map: ILevelMapAggregatedTile[][]) {
    if (!tile || !tile.tileType)
        return;

    if (tile.isRevealed  && tile.tileType !== TileType.Exit)
        return;

    let progress = [{
        id: tile.id,
        isRevealed: true
    }];

    if (tile.tileType === TileType.Exit) {
        const connectedTilesProgress = yield call(revealConnectedTiles, tile, map);

        progress = [
            ...progress,
            ...connectedTilesProgress
        ]
    }

    return progress;
}

export function* revealConnectedTiles(tile: ILevelMapAggregatedTile, map: ILevelMapAggregatedTile[][]): Generator<any, any, any> {
    const row = tile.coords.y;
    const column = tile.coords.x;

    const topTile = map[row + 1] && map[row + 1][column];
    const rightTile = map[row][column + 1];
    const bottomTile = map[row - 1] && map[row - 1][column];
    const leftTile = map[row][column - 1];

    const progress = [
        yield call(revealTileIfNeeded, topTile, map),
        yield call(revealTileIfNeeded, rightTile, map),
        yield call(revealTileIfNeeded, bottomTile, map),
        yield call(revealTileIfNeeded, leftTile, map)
    ] as ILevelMapTileProgress[];

    return Array.prototype.concat.apply([], progress.filter(tileProgress => tileProgress));
}

export function* processConnectedTiles(tile: ILevelMapAggregatedTile) {
    type GameStateSlice = {
        levelMap: ILevelMap;
        questions: Record<number, IAggregatedQuestion>;
        levelMapTileProgress: Record<number, ILevelMapTileProgress>;
        levelMapProgress: ILevelMapProgress;
    };

    const {levelMap, questions, levelMapTileProgress, levelMapProgress}: GameStateSlice = yield select(({ game }: IApplicationState) => ({
        questions: game.questions,
        levelMap: game.levelMaps[game.levelInProgress],
        levelMapProgress: game.levelMapProgress[game.levelInProgress],
        levelMapTileProgress: game.levelMapTileProgress,
    }));

    const level = levelMap;
    const map = level.tiles.map(row => {
        return row.map(tile => ({
            ...tile,
            ...levelMapTileProgress[tile.id]
        }));
    });
    const unrevealedTiles = map.flatMap(row => row).filter(tile => tile.tileType && !tile.isRevealed);

    const connectedTilesProgress = yield call(revealConnectedTiles, tile, map);

    if (!connectedTilesProgress.length)
        return;

    yield fork(preloadTileImages, questions, level.tiles, connectedTilesProgress);

    const isExitFound = levelMapProgress.isExitFound
        || connectedTilesProgress.some((tile: ILevelMapTileProgress) => tile.id === level.exitTileId);

    const allTilesAreRevealed = levelMapProgress.allTilesAreRevealed
        || !unrevealedTiles.some(unrevealedTile => !connectedTilesProgress.some((tile: ILevelMapTileProgress) => tile.id === unrevealedTile.id));

    yield put(setLevelProgress({
        isExitFound: isExitFound,
        allTilesAreRevealed: !!allTilesAreRevealed,
        levelProgress: makeMap(connectedTilesProgress, "id")
    }));
}